import { useContext, useEffect, useState } from "react";
import { useFormik, Form, FormikProvider } from "formik";
import * as Yup from "yup";
import {
  Grid,
  Dialog,
  Button,
  TextField,
  Typography,
  DialogContent,
  FormHelperText,
} from "@mui/material";
import moment from "moment";
//Import from project
import "../../Style/dialogstyle.scss";
import { AuthContext } from "../../Context/AuthContext";
import { DialogTitleComp } from "../../Utils/Component";
import useExpenseForm from "./Hooks/useExpenseForm";
import { SelectDatePicker } from "../Include/SelectDatePicker/SelectDatePicker";
import { SelectExpenseType } from "../Include/DynamicSelect/DynamicSelect";

export default function ExpenseForm({
  open,
  editData,
  setRefetch,
  dialogTitle,
  handleClose,
  hideBackdrop = false,
}) {
  // Change Language
  const { t, language } = useContext(AuthContext);
  // const [amountUSD, setAmountUSD] = useState(0);

  //fromik
  const CheckValidation = Yup.object().shape({
    type: Yup.object().shape({
      id: Yup.string().required(t("required")),
      title: Yup.string(),
    }),
    date: Yup.string().required(t("required")),
    exchangeRate: Yup.string().required(t("required")),
    amountUSD: Yup.string(),
    amountKHR: Yup.string(),
    remark: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      type: { id: "", title: "" },
      date: moment(),
      exchangeRate: 4000,
      amountUSD: 0,
      amountKHR: 0,
      remark: "",
    },
    validationSchema: CheckValidation,
    onSubmit: async (values) => {
      setLoading(true);
      if (dialogTitle === "Create") {
        createExpense({
          variables: {
            input: {
              ...values,
              type: values?.type?.id,
            },
          },
        });
      } else {
        // console.log("values::", values)
        updateExpense({
          variables: {
            id: editData?._id,
            input: {
              ...values,
              type: values?.type?.id,
            },
          },
        });
      }
    },
  });

  const {
    errors,
    values,
    touched,
    resetForm,
    handleSubmit,
    setFieldValue,
    getFieldProps,
  } = formik;

  console.log("editData::", editData);

  const { createExpense, loading, updateExpense, setLoading } = useExpenseForm({
    resetForm,
    setRefetch,
    handleClose,
  });

  useEffect(() => {
    if (dialogTitle === "Update" && editData) {
      setFieldValue("date", editData?.date);
      setFieldValue("exchangeRate", editData?.exchangeRate);
      setFieldValue("amountUSD", editData?.amountUSD);
      setFieldValue("amountKHR", editData?.amountKHR);
      setFieldValue("remark", editData?.remark);
    }

    if (editData?.type?._id) {
      setFieldValue("type.id", editData?.type?._id);
      setFieldValue(
        "type.title",
        language === "en" ? editData?.type?.engType : editData?.type?.khType,
      );
    }

    if (dialogTitle === "Create" && open) {
      resetForm();
    }
  }, [open, editData]);

  return (
    <Dialog
      open={open}
      className="dialog-container"
      maxWidth="md"
      fullWidth
      hideBackdrop={hideBackdrop}
    >
      <DialogTitleComp
        title={
          dialogTitle === "Create"
            ? t("modal_title_create_expense")
            : t("modal_title_update_expense")
        }
        classTitle={"dialog-title"}
        classIcon={"close-icon"}
        handleClose={handleClose}
      />

      <DialogContent dividers>
        <FormikProvider value={formik}>
          <Form noValidate autoComplete="off" onSubmit={handleSubmit}>
            <Grid container rowSpacing={2} columnSpacing={3}>
              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_date")}
                </Typography>
                <SelectDatePicker
                  value={values?.date}
                  onChange={(val) => setFieldValue("date", val || null)}
                />
              </Grid>
              <Grid item xs={4}>
                <SelectExpenseType
                  value={values?.type}
                  onChange={(val) => {
                    // console.log("val::", val);
                    if (val.id) {
                      setFieldValue("type", val);
                    } else {
                      setFieldValue("type.id", "");
                    }
                  }}
                  className="text-field"
                />
                {!!errors.type?.id && touched.type?.id && (
                  <FormHelperText error id="outlined-adornment-password">
                    {errors.type?.id}
                  </FormHelperText>
                )}
              </Grid>
              <Grid item xs={4}></Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_exchange_rate")} (៛)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        ៛
                      </Typography>
                    ),
                  }}
                  className="text-field"
                  placeholder={t("thead_exchange_rate")}
                  value={values?.exchangeRate}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setFieldValue("exchangeRate", 0);
                    } else {
                      setFieldValue("exchangeRate", inputVal);
                    }
                  }}
                  error={Boolean(touched.exchangeRate && errors.exchangeRate)}
                  helperText={touched.exchangeRate && errors.exchangeRate}
                />
              </Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_amount")} ($)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        $
                      </Typography>
                    ),
                  }}
                  className="text-field"
                  placeholder={t("thead_input_amount")}
                  value={values?.amountUSD}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setFieldValue("amountUSD", 0);
                    } else {
                      setFieldValue("amountUSD", inputVal);
                    }
                  }}
                  error={Boolean(touched.amountUSD && errors.amountUSD)}
                  helperText={touched.amountUSD && errors.amountUSD}
                />
              </Grid>

              <Grid item xs={4}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_amount")} (៛)
                </Typography>
                <TextField
                  type="number"
                  fullWidth
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <Typography className={t("class-name-field-title")}>
                        ៛
                      </Typography>
                    ),
                  }}
                  className="text-field"
                  placeholder={t("thead_input_amount")}
                  value={values?.amountKHR}
                  onChange={(e) => {
                    const inputVal = parseFloat(e.target.value);
                    if (inputVal <= 0) {
                      setFieldValue("amountKHR", 0);
                    } else {
                      setFieldValue("amountKHR", inputVal);
                    }
                  }}
                  error={Boolean(touched.amountKHR && errors.amountKHR)}
                  helperText={touched.amountKHR && errors.amountKHR}
                />
              </Grid>

              <Grid item xs={12}>
                <Typography className={t("class-name-field-title")}>
                  {t("thead_description")}
                </Typography>
                <TextField
                  className="text-field"
                  size="small"
                  rows={2}
                  multiline
                  fullWidth
                  placeholder={t("thead_description")}
                  {...getFieldProps("remark")}
                />
              </Grid>

              <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
                {loading ? (
                  <Button className={t("class-btn-create")} fullWidth>
                    {t("loading")}
                  </Button>
                ) : (
                  <Button
                    fullWidth
                    onClick={handleSubmit}
                    className={t("class-btn-create")}
                  >
                    {dialogTitle === "Update" ? t("update") : t("create")}
                  </Button>
                )}
              </Grid>
            </Grid>
          </Form>
        </FormikProvider>
      </DialogContent>
    </Dialog>
  );
}
